// Tunables and content tables. Everything the designer would want to twiddle
// lives here; the systems that read it live in game.ts and its neighbours.

export const MAP_W = 56;
export const MAP_H = 44;

/** Seconds of real time per in-game hour. */
export const HOUR_SECONDS = 11;
export const DAY_START_HOUR = 6;
export const DAY_END_HOUR = 26; // 2am, then you pass out

export const PLAYER_SPEED = 4.2; // tiles per second
export const PLAYER_RADIUS = 0.3;

export const CAN_CAPACITY = 24;
export const START_GOLD = 150;

export const TREE_WOOD = 3;
export const WOOD_PRICE = 12;
export const STONE_PRICE = 8;

/** How far the player can reach when clicking a tile rather than facing it. */
export const REACH_TILES = 2.4;

// ------------------------------------------------------------------- energy

export const ENERGY_MAX = 100;
/** Faint at zero: you lose the rest of the day and a slice of your purse. */
export const FAINT_GOLD_PENALTY = 0.1;
export const FAINT_ENERGY_REFUND = 0.45;
/** A full night's sleep restores this fraction; a collapse restores less. */
export const SLEEP_RESTORE = 1;

// ------------------------------------------------------------------ seasons

export type SeasonId = "spring" | "summer" | "autumn" | "winter";

export const SEASONS: SeasonId[] = ["spring", "summer", "autumn", "winter"];
export const SEASON_DAYS = 28;

export const SEASON_NAMES: Record<SeasonId, string> = {
  spring: "Spring",
  summer: "Summer",
  autumn: "Autumn",
  winter: "Winter",
};

/** Ambient wash multiplied into the day/night tint. */
export const SEASON_TINT: Record<SeasonId, [number, number, number]> = {
  spring: [1, 1, 1],
  summer: [1.04, 1.0, 0.92],
  autumn: [1.02, 0.94, 0.86],
  winter: [0.9, 0.95, 1.06],
};

export function seasonOf(day: number): SeasonId {
  return SEASONS[Math.floor((day - 1) / SEASON_DAYS) % SEASONS.length]!;
}

export function dayOfSeason(day: number): number {
  return ((day - 1) % SEASON_DAYS) + 1;
}

export function yearOf(day: number): number {
  return Math.floor((day - 1) / (SEASON_DAYS * SEASONS.length)) + 1;
}

// ------------------------------------------------------------------ weather

export type WeatherId = "clear" | "rain" | "storm" | "snow" | "wind";

export interface WeatherDef {
  id: WeatherId;
  name: string;
  icon: string;
  /** waters every soil tile at dawn */
  waters: boolean;
  /** dims the day and mutes the palette */
  tint: [number, number, number];
}

export const WEATHER: Record<WeatherId, WeatherDef> = {
  clear: { id: "clear", name: "Clear", icon: "ui_weather_sun", waters: false, tint: [1, 1, 1] },
  wind: { id: "wind", name: "Windy", icon: "ui_weather_sun", waters: false, tint: [0.98, 0.99, 1] },
  rain: { id: "rain", name: "Rain", icon: "ui_weather_rain", waters: true, tint: [0.74, 0.79, 0.9] },
  storm: { id: "storm", name: "Storm", icon: "ui_weather_storm", waters: true, tint: [0.6, 0.64, 0.8] },
  snow: { id: "snow", name: "Snow", icon: "ui_weather_snow", waters: false, tint: [0.86, 0.9, 1.02] },
};

/** Per-season odds, rolled the night before so the forecast can be honest. */
export const WEATHER_ODDS: Record<SeasonId, [WeatherId, number][]> = {
  spring: [
    ["rain", 0.3],
    ["wind", 0.15],
    ["storm", 0.05],
    ["clear", 1],
  ],
  summer: [
    ["rain", 0.14],
    ["storm", 0.12],
    ["wind", 0.1],
    ["clear", 1],
  ],
  autumn: [
    ["rain", 0.26],
    ["wind", 0.22],
    ["storm", 0.08],
    ["clear", 1],
  ],
  winter: [
    ["snow", 0.45],
    ["wind", 0.15],
    ["clear", 1],
  ],
};

// -------------------------------------------------------------------- tools

export type ToolId = "hoe" | "wateringcan" | "seedbag" | "shears" | "axe" | "pickaxe" | "fishingrod";

export type TierId = "basic" | "copper" | "steel" | "gold";

export interface TierDef {
  id: TierId;
  name: string;
  /** tiles hit per swing: 1 = the faced tile, 3 = a row, 9 = a 3x3 block */
  area: 1 | 3 | 9;
  /** multiplies the tool's base energy cost */
  energy: number;
  /** multiplies the swing duration */
  speed: number;
  /** gold, wood and ore to step up to this tier */
  cost: { gold: number; wood: number; ore: number } | null;
}

export const TIERS: TierDef[] = [
  { id: "basic", name: "Rusty", area: 1, energy: 1, speed: 1, cost: null },
  { id: "copper", name: "Copper", area: 1, energy: 0.8, speed: 0.85, cost: { gold: 400, wood: 10, ore: 5 } },
  { id: "steel", name: "Steel", area: 3, energy: 0.65, speed: 0.75, cost: { gold: 1800, wood: 20, ore: 12 } },
  { id: "gold", name: "Gold", area: 9, energy: 0.5, speed: 0.6, cost: { gold: 6000, wood: 30, ore: 25 } },
];

export const TIER_BY_ID = new Map(TIERS.map((t) => [t.id, t]));

export interface ToolDef {
  id: ToolId;
  name: string;
  sprite: string;
  /** character animation prefix in the sprite pack */
  anim: string;
  /** seconds the swing takes */
  duration: number;
  /** stamina a swing costs at the basic tier */
  energy: number;
  /** upgradable tools have tiers; the seed bag and shears do not */
  upgradable: boolean;
  hint: string;
}

export const TOOLS: ToolDef[] = [
  { id: "hoe", name: "Hoe", sprite: "tool_hoe", anim: "hoe", duration: 0.45, energy: 4, upgradable: true, hint: "till grass into soil" },
  { id: "wateringcan", name: "Watering Can", sprite: "tool_wateringcan", anim: "water", duration: 0.5, energy: 2, upgradable: true, hint: "water soil · refill at the pond" },
  { id: "seedbag", name: "Seed Bag", sprite: "tool_seedbag", anim: "harvest", duration: 0.35, energy: 1, upgradable: false, hint: "plant the selected seed" },
  { id: "shears", name: "Shears", sprite: "tool_shears", anim: "harvest", duration: 0.4, energy: 2, upgradable: false, hint: "harvest ripe crops · forage" },
  { id: "axe", name: "Axe", sprite: "tool_axe", anim: "axe", duration: 0.55, energy: 6, upgradable: true, hint: "chop trees · clear dead crops" },
  { id: "pickaxe", name: "Pickaxe", sprite: "tool_pickaxe", anim: "pickaxe", duration: 0.55, energy: 6, upgradable: true, hint: "break rocks and ore in the mine" },
  { id: "fishingrod", name: "Fishing Rod", sprite: "tool_fishingrod", anim: "farmer_cast", duration: 0.5, energy: 1, upgradable: false, hint: "cast into open water" },
];

export const TOOL_BY_ID = new Map(TOOLS.map((t) => [t.id, t]));

/** Sprite id for a tool at a tier, falling back to the untiered icon. */
export function toolSprite(tool: ToolDef, tier: TierId): string {
  return tool.upgradable && tier !== "basic" ? `${tool.sprite}_${tier}` : tool.sprite;
}

// -------------------------------------------------------------------- crops

export interface CropDef {
  id: string;
  name: string;
  seedCost: number;
  price: number;
  /** days needed to leave stage 0, 1 and 2 */
  stageDays: [number, number, number];
  /** ripe again after harvest instead of clearing the tile */
  regrow?: boolean;
  /** seasons it will grow in; a greenhouse ignores this */
  seasons: SeasonId[];
}

export const CROPS: CropDef[] = [
  { id: "carrot", name: "Carrot", seedCost: 12, price: 32, stageDays: [1, 1, 1], seasons: ["spring"] },
  { id: "wheat", name: "Wheat", seedCost: 10, price: 28, stageDays: [1, 1, 1], seasons: ["summer", "autumn"] },
  { id: "onion", name: "Onion", seedCost: 16, price: 40, stageDays: [1, 1, 2], seasons: ["spring"] },
  { id: "potato", name: "Potato", seedCost: 18, price: 48, stageDays: [1, 2, 1], seasons: ["spring"] },
  { id: "sunflower", name: "Sunflower", seedCost: 22, price: 58, stageDays: [1, 2, 1], seasons: ["summer", "autumn"] },
  { id: "strawberry", name: "Strawberry", seedCost: 45, price: 42, stageDays: [1, 2, 1], regrow: true, seasons: ["spring"] },
  { id: "cabbage", name: "Cabbage", seedCost: 28, price: 80, stageDays: [2, 2, 1], seasons: ["spring", "autumn"] },
  { id: "corn", name: "Corn", seedCost: 32, price: 92, stageDays: [2, 2, 2], seasons: ["summer", "autumn"] },
  { id: "tomato", name: "Tomato", seedCost: 55, price: 62, stageDays: [2, 2, 2], regrow: true, seasons: ["summer"] },
  { id: "eggplant", name: "Eggplant", seedCost: 48, price: 135, stageDays: [2, 2, 3], seasons: ["autumn"] },
  { id: "grapes", name: "Grapes", seedCost: 75, price: 95, stageDays: [2, 3, 3], regrow: true, seasons: ["summer", "autumn"] },
  { id: "pumpkin", name: "Pumpkin", seedCost: 60, price: 190, stageDays: [3, 2, 3], seasons: ["autumn"] },
];

export const CROP_BY_ID = new Map(CROPS.map((c) => [c.id, c]));

export const STAGE_NAMES = ["seed", "sprout", "young", "ripe", "withered"] as const;

/** Sprite id for a crop stage, e.g. "tomato_3_ripe". */
export function cropSprite(cropId: string, stage: number): string {
  return `${cropId}_${stage}_${STAGE_NAMES[stage]}`;
}

// -------------------------------------------------------- quality and soil

/** Silver and gold crops sell for more. Index is `Crop.quality`. */
export const QUALITY_NAMES = ["", "Silver", "Gold"] as const;
export const QUALITY_MULT = [1, 1.25, 1.5];

/** Soil fertility runs 0..100. Planting the same crop twice in a row costs more. */
export const SOIL_START = 55;
export const SOIL_HARVEST_DRAIN = 12;
export const SOIL_REPEAT_DRAIN = 8;
export const SOIL_FALLOW_GAIN = 3;

export interface FertiliserDef {
  key: string;
  name: string;
  sprite: string;
  cost: number;
  /** fertility added when applied */
  boost: number;
}

export const FERTILISERS: FertiliserDef[] = [
  { key: "fertiliser:basic", name: "Basic Fertiliser", sprite: "item_fertiliser_basic", cost: 24, boost: 25 },
  { key: "fertiliser:quality", name: "Quality Fertiliser", sprite: "item_fertiliser_quality", cost: 70, boost: 55 },
];

/** Odds of silver / gold at a given soil fertility. */
export function qualityOdds(fertility: number): [number, number] {
  const f = Math.max(0, Math.min(100, fertility)) / 100;
  return [0.1 + f * 0.45, f * f * 0.3];
}

// ------------------------------------------------------------------ fishing

export interface FishDef {
  id: string;
  name: string;
  sprite: string;
  price: number;
  /** relative chance of being on the end of the line */
  weight: number;
  /** how hard the bar is to hold, 0..1 */
  difficulty: number;
}

export const FISH: FishDef[] = [
  { id: "bass", name: "Bass", sprite: "item_fish_bass", price: 65, weight: 4, difficulty: 0.35 },
  { id: "carp", name: "Carp", sprite: "item_fish_carp", price: 34, weight: 5, difficulty: 0.2 },
  { id: "trout", name: "Trout", sprite: "item_fish_trout", price: 110, weight: 2, difficulty: 0.6 },
  { id: "boot", name: "Old Boot", sprite: "item_fish_boot", price: 2, weight: 2, difficulty: 0.1 },
];

export const FISH_BY_ID = new Map(FISH.map((f) => [f.id, f]));

/** Seconds of waiting before a bite, and how long the window stays open. */
export const BITE_MIN = 1.4;
export const BITE_MAX = 5.5;
export const BITE_WINDOW = 1.1;
/** The landing bar: how long you have to keep the marker inside the zone. */
export const REEL_TIME = 4.5;
export const REEL_NEEDED = 1;

// ------------------------------------------------------------------ forage

export interface ForageDef {
  key: string;
  name: string;
  sprite: string;
  price: number;
  /** stamina restored when eaten, 0 if it is not food */
  food: number;
}

export const FORAGE: Record<string, ForageDef> = {
  "forage:berry": { key: "forage:berry", name: "Wild Berries", sprite: "item_berry", price: 28, food: 12 },
  "forage:mushroom": { key: "forage:mushroom", name: "Mushroom", sprite: "item_mushroom", price: 44, food: 18 },
  "forage:stick": { key: "forage:stick", name: "Stick", sprite: "item_stick", price: 4, food: 0 },
  "forage:stone": { key: "forage:stone", name: "Stone", sprite: "item_stone", price: 6, food: 0 },
  "forage:flower": { key: "forage:flower", name: "Wild Flower", sprite: "wild_flower", price: 22, food: 0 },
};

/** How many forage props the world tries to keep alive, and the daily respawn. */
export const FORAGE_TARGET = 26;
export const FORAGE_PER_DAY = 5;
export const WEED_CHANCE = 0.06;

// ----------------------------------------------------------------- animals

export type AnimalKind = "chicken" | "cow";

export interface AnimalDef {
  kind: AnimalKind;
  name: string;
  cost: number;
  /** how many tiles it wanders per second */
  speed: number;
  /** item laid each morning once it is fed and happy */
  produce: string;
  produceName: string;
  producePrice: number;
  /** hearts needed before it produces at all */
  minAffection: number;
  /** sprite prefix; frames are `${prefix}_${pose}_${dir}_${frame}` */
  prefix: string;
  /** how many tiles wide the sprite is, for the shadow */
  shadow: number;
}

export const ANIMALS: Record<AnimalKind, AnimalDef> = {
  chicken: {
    kind: "chicken",
    name: "Chicken",
    cost: 320,
    speed: 0.9,
    produce: "animal:egg",
    produceName: "Egg",
    producePrice: 55,
    minAffection: 1,
    prefix: "chicken",
    shadow: 0.6,
  },
  cow: {
    kind: "cow",
    name: "Cow",
    cost: 1200,
    speed: 0.6,
    produce: "animal:milk",
    produceName: "Milk",
    producePrice: 165,
    minAffection: 2,
    prefix: "cow",
    shadow: 1.1,
  },
};

export const ANIMAL_MAX_AFFECTION = 5;
export const ANIMAL_FEED_COST = 18;

// --------------------------------------------------------------- buildables

export interface BuildDef {
  id: string;
  name: string;
  /** what it becomes: a prop, or a ground tile */
  kind: "prop" | "tile";
  /** Prop enum value or Tile enum value, resolved in world.ts */
  value: number;
  cost: { gold?: number; wood?: number; stone?: number };
  sprite: string;
  hint: string;
  /** may only be placed on water */
  water?: boolean;
}

/**
 * The build menu. `value` refers to the Prop or Tile enums in world.ts; the
 * numbers are kept here rather than imported to keep config free of cycles.
 */
export const BUILDABLES: BuildDef[] = [
  { id: "fence", name: "Fence", kind: "prop", value: 1, cost: { wood: 2 }, sprite: "fence_h", hint: "keeps animals in" },
  { id: "gate", name: "Gate", kind: "prop", value: 20, cost: { wood: 4 }, sprite: "gate_closed", hint: "walk through a fence line" },
  { id: "gravel", name: "Gravel Path", kind: "tile", value: 7, cost: { stone: 1 }, sprite: "path_gravel", hint: "nothing grows on it" },
  { id: "plank", name: "Plank Path", kind: "tile", value: 8, cost: { wood: 1 }, sprite: "path_plank", hint: "nothing grows on it" },
  { id: "chest", name: "Chest", kind: "prop", value: 21, cost: { wood: 12 }, sprite: "chest", hint: "stores what your satchel cannot" },
  { id: "scarecrow", name: "Scarecrow", kind: "prop", value: 22, cost: { wood: 18, gold: 40 }, sprite: "scarecrow", hint: "keeps crows off nearby crops" },
  { id: "sprinkler", name: "Sprinkler", kind: "prop", value: 23, cost: { gold: 380, wood: 6, stone: 8 }, sprite: "sprinkler", hint: "waters its four neighbours at dawn" },
  { id: "coop", name: "Chicken Coop", kind: "prop", value: 24, cost: { gold: 900, wood: 40, stone: 10 }, sprite: "coop", hint: "chickens live here" },
  { id: "trough", name: "Feed Trough", kind: "prop", value: 25, cost: { wood: 8 }, sprite: "feed_trough", hint: "fill it and the animals eat" },
  { id: "barn", name: "Barn", kind: "prop", value: 26, cost: { gold: 2600, wood: 90, stone: 40 }, sprite: "barn", hint: "cows live here" },
  { id: "bridge", name: "Bridge", kind: "tile", value: 9, cost: { wood: 4 }, sprite: "bridge_h", hint: "cross the pond", water: true },
  { id: "hay", name: "Hay Bale", kind: "prop", value: 27, cost: { wood: 3 }, sprite: "hay_bale", hint: "decoration, mostly" },
  { id: "mailbox", name: "Mailbox", kind: "prop", value: 28, cost: { wood: 5 }, sprite: "mailbox", hint: "for the letters you never get" },
  { id: "well", name: "Well", kind: "prop", value: 29, cost: { gold: 260, stone: 24 }, sprite: "well", hint: "refill the can without the walk" },
];

export const BUILD_BY_ID = new Map(BUILDABLES.map((b) => [b.id, b]));

/** Crows raid crops overnight unless a scarecrow stands within this radius. */
export const SCARECROW_RADIUS = 8;
export const CROW_CHANCE = 0.12;

// ------------------------------------------------------------ farm regions

export interface RegionDef {
  id: string;
  name: string;
  price: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  blurb: string;
  /** crops inside ignore the season */
  greenhouse?: boolean;
}

export const REGIONS: RegionDef[] = [
  { id: "east", name: "The East Plot", price: 2500, x0: 36, y0: 20, x1: 53, y1: 38, blurb: "A second field, fenced and fallow." },
  { id: "woods", name: "The North Woods", price: 4200, x0: 24, y0: 2, x1: 36, y1: 11, blurb: "Old woodland. Clear it and it is yours." },
  { id: "glass", name: "The Greenhouse", price: 9000, x0: 2, y0: 24, x1: 10, y1: 34, blurb: "Glass and iron. Seasons stop at the door.", greenhouse: true },
];

export const REGION_BY_ID = new Map(REGIONS.map((r) => [r.id, r]));

// ------------------------------------------------------------------- mine

export const MINE_FLOORS = 6;
/** Ore odds deepen with the floor. */
export const ORE_TABLE: [string, number, number][] = [
  // key, price, chance at floor 1 (scaled by floor)
  ["ore:copper", 40, 0.5],
  ["ore:iron", 95, 0.25],
  ["ore:gold", 220, 0.08],
];

// -------------------------------------------------------------------- shop

/** Daily "wanted" request from the shopkeeper: a crop at a premium. */
export const REQUEST_MULTIPLIER = 2.4;
export const REQUEST_MIN_QTY = 2;
export const REQUEST_MAX_QTY = 6;

// ------------------------------------------------------------ achievements

export interface AchievementDef {
  id: string;
  name: string;
  blurb: string;
}

export const ACHIEVEMENTS: AchievementDef[] = [
  { id: "first_harvest", name: "First Harvest", blurb: "Cut a ripe crop with the shears." },
  { id: "first_ship", name: "Open for Business", blurb: "Get paid for a crate of produce." },
  { id: "green_thumb", name: "Green Thumb", blurb: "Grow a gold-quality crop." },
  { id: "angler", name: "Angler", blurb: "Land your first fish." },
  { id: "trout_tickler", name: "Trout Tickler", blurb: "Land a trout." },
  { id: "lumberjack", name: "Lumberjack", blurb: "Fell fifty trees." },
  { id: "spelunker", name: "Spelunker", blurb: "Reach the bottom of the mine." },
  { id: "rancher", name: "Rancher", blurb: "Collect produce from an animal." },
  { id: "builder", name: "Builder", blurb: "Place ten things with the build menu." },
  { id: "landowner", name: "Landowner", blurb: "Buy a plot of land." },
  { id: "four_seasons", name: "Four Seasons", blurb: "See a full year turn." },
  { id: "moneybags", name: "Moneybags", blurb: "Hold ten thousand gold at once." },
];

// --------------------------------------------------------------------- misc

export const SAVE_SLOTS = 3;
export const AUTOSAVE_SECONDS = 30;
